'use client';

import { useRovingRadioGroup } from '@/hooks/useRovingRadioGroup';
import { cn } from '@/lib/utils';
import { Briefcase, Building2, User } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

export type PricingAudience = 'individuals' | 'solo_practitioner' | 'firm';

interface AudienceSwitcherProps {
  audience: PricingAudience;
  onChange: (audience: PricingAudience) => void;
}

/**
 * Primary audience selector on /pricing — MCP-first positioning.
 *
 * Audience is the FIRST decision a visitor makes; billing cadence
 * (Monthly/Annual/Pack) lives inside each audience.
 *
 * Structure:
 * - Individuals: MCP access via Claude/ChatGPT, with web app as secondary UI
 * - Solo practitioners: directory listing + AI research bundled
 * - Firms & Teams: seat-based pricing for law offices and in-house teams
 *
 * Mirrors PricingModeToggle's sliding-indicator pattern (CSS index-driven
 * width + translateX over equal-width segments) so the two segmented controls
 * read as a coherent stack.
 */
export function AudienceSwitcher({
  audience,
  onChange,
}: AudienceSwitcherProps) {
  const t = useTranslations('PricingPage');

  const segments: {
    key: PricingAudience;
    labelKey:
      | 'audienceIndividuals'
      | 'audienceSoloPractitioner'
      | 'audienceFirm';
    Icon: typeof User;
  }[] = [
    { key: 'individuals', labelKey: 'audienceIndividuals', Icon: User },
    {
      key: 'solo_practitioner',
      labelKey: 'audienceSoloPractitioner',
      Icon: Briefcase,
    },
    { key: 'firm', labelKey: 'audienceFirm', Icon: Building2 },
  ];

  // CSS-driven sliding indicator: segments are equal-width grid columns, so
  // the indicator's geometry is a pure function of the active index — no DOM
  // measurement, no useLayoutEffect. The SSR markup is already correct, which
  // kills the zero-size flash on first paint (#5014). The `transition` on
  // ``transform`` still animates smoothly between segments.
  const segmentCount = segments.length;
  const activeIndex = Math.max(
    0,
    segments.findIndex((s) => s.key === audience)
  );
  const segmentWidthPct = 100 / segmentCount;

  // Roving-tabindex keyboard nav (ARIA APG radio-group pattern, #5013): the
  // segmented control is a single Tab stop and Arrow / Home / End move both
  // selection and focus. Refs let focus follow the freshly selected segment.
  const segmentRefs = useRef<
    Partial<Record<PricingAudience, HTMLButtonElement>>
  >({});
  const radio = useRovingRadioGroup<PricingAudience>({
    keys: segments.map((s) => s.key),
    selected: audience,
    onSelect: onChange,
    refs: segmentRefs,
  });

  return (
    <div className='flex flex-col items-center justify-center'>
      <div
        role='radiogroup'
        aria-label={t('audienceAriaLabel')}
        className='relative inline-flex rounded-full border border-line-default bg-surface-card p-1 shadow-sm'
      >
        {/* Inner track — zero-padding content box that hosts the
            absolutely-positioned indicator and the equal-width segment grid,
            so the indicator percentages resolve exactly and measurement-free. */}
        <div className='relative grid grid-flow-col auto-cols-fr items-center'>
          {/* Sliding indicator — sized/positioned from the active index in
              CSS, so it renders correctly on the SSR first paint (no
              measurement, no zero-size flash #5014). */}
          <div
            className='absolute top-0 left-0 h-full rounded-full [background:var(--gradient-button)] shadow-sm transition-transform duration-300 ease-out'
            style={{
              width: `${segmentWidthPct}%`,
              transform: `translateX(${activeIndex * 100}%)`,
            }}
            aria-hidden='true'
          />

          {segments.map(({ key, labelKey, Icon }) => {
            const { tabIndex, onKeyDown } = radio(key);
            return (
              <button
                key={key}
                ref={(el) => {
                  if (el) segmentRefs.current[key] = el;
                  else delete segmentRefs.current[key];
                }}
                type='button'
                role='radio'
                aria-checked={audience === key}
                tabIndex={tabIndex}
                onClick={() => onChange(key)}
                onKeyDown={onKeyDown}
                className={cn(
                  'relative z-10 px-4 sm:px-6 py-2.5 text-sm font-semibold rounded-full transition-colors duration-200 min-h-[44px] whitespace-nowrap inline-flex items-center justify-center gap-2',
                  audience === key
                    ? 'text-cta-text'
                    : 'text-content-secondary hover:text-content-primary'
                )}
              >
                <Icon className='h-4 w-4' aria-hidden='true' />
                {t(labelKey)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
